import { useState, useEffect, useCallback } from "react";
import {
  getDocs,
  getDocumentsByArrayMembershipInCollection,
} from "../globals/services";

export const useStackCollections = (pageSize = 20) => {
  const [stackCategories, setStackCategories] = useState([]);
  const [filteredStackCategories, setFilteredStackCategories] = useState([]);
  const [subCollections, setSubCollections] = useState({}); // Cache for subcollections
  const [filteredSubCollections, setFilteredSubCollections] = useState({}); // Filtered subcollections
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadingSub, setLoadingSub] = useState({}); // Loading state for each subcollection
  const [lastVisible, setLastVisible] = useState(null);
  const [error, setError] = useState(null);
  const [refreshCounter, setRefreshCounter] = useState(0);
  const [unsubscribes, setUnsubscribes] = useState({});

  // Search and filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all, active, inactive
  const [subSearchQueries, setSubSearchQueries] = useState({}); // categoryId -> searchQuery
  const [subStatusFilters, setSubStatusFilters] = useState({}); // categoryId -> statusFilter

  const fetchData = useCallback(
    async (startAfterDoc = null) => {
      if (startAfterDoc) setLoadingMore(true);
      else setLoading(true);
      setError(null);

      try {
        const result = await getDocs(
          "techStacks150r",
          false,
          pageSize,
          "name",
          startAfterDoc
        );
        const newDocs = result.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
          ref: doc.ref,
        }));

        if (startAfterDoc) {
          setStackCategories((prev) => [...prev, ...newDocs]);
        } else {
          setStackCategories(newDocs);
        }

        if (result.docs.length > 0) {
          setLastVisible(result.docs[result.docs.length - 1]);
        } else {
          setLastVisible(null);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        if (startAfterDoc) setLoadingMore(false);
        else setLoading(false);
      }
    },
    [pageSize]
  );

  // Apply status filter helper function
  const applyStatusFilter = useCallback((items, filter) => {
    if (filter === "all") return items;

    return items.filter((item) => {
      if (filter === "active") {
        return item.show === true;
      } else if (filter === "inactive") {
        return item.show === false;
      }
      return true;
    });
  }, []);

  // Search function for main collections
  const searchCollections = useCallback(
    async (query, currentStatusFilter) => {
      if (!query.trim()) {
        // No search query, just apply status filter to all items
        const filtered = applyStatusFilter(
          stackCategories,
          currentStatusFilter
        );
        setFilteredStackCategories(filtered);
        return;
      }

      try {
        const searchTerm = query.toLowerCase();
        const collectionIds = new Set();

        // 1. Search collection names directly
        const matchingCollections = stackCategories.filter((collection) => {
          if (collection.nameIndex && Array.isArray(collection.nameIndex)) {
            return collection.nameIndex.some((term) =>
              term.toLowerCase().includes(searchTerm)
            );
          }
          return collection.name?.toLowerCase().includes(searchTerm);
        });

        matchingCollections.forEach((collection) => {
          collectionIds.add(collection.id);
        });

        // 2. Search sub-items (technologies) across all collections using collection group
        try {
          const techResults = await getDocumentsByArrayMembershipInCollection(
            "technologies",
            searchTerm,
            ["nameIndex"],
            "rank",
            true, // group query
            true,
            false,
            100
          );

          // Extract parent collection IDs from technology documents
          techResults.forEach((doc) => {
            const parentCollectionId = doc.ref.parent.parent.id;
            collectionIds.add(parentCollectionId);
          });
        } catch (error) {
          console.error("Error searching technologies:", error);
        }

        // 3. Filter original collections to only include those with matching IDs
        const filteredCollections = stackCategories.filter((collection) =>
          collectionIds.has(collection.id)
        );

        // Apply status filter to search results
        const filtered = applyStatusFilter(
          filteredCollections,
          currentStatusFilter
        );
        setFilteredStackCategories(filtered);
      } catch (error) {
        console.error("Error searching collections:", error);
        setFilteredStackCategories([]);
      }
    },
    [stackCategories, applyStatusFilter]
  );

  // Filter and search effect for main collections
  useEffect(() => {
    // Always call searchCollections which handles both search and filter
    searchCollections(searchQuery, statusFilter);
  }, [stackCategories, statusFilter, searchQuery, searchCollections]);

  // Filter sub-collections (includes global search filter)
  const filterSubCollection = useCallback(
    (categoryId, subItems) => {
      const localSearchQuery = subSearchQueries[categoryId] || "";
      const localStatusFilter = subStatusFilters[categoryId] || "all";

      let filtered = subItems || [];

      // Apply status filter
      if (localStatusFilter !== "all") {
        filtered = filtered.filter((item) => {
          if (localStatusFilter === "active") {
            return item.show === true;
          } else if (localStatusFilter === "inactive") {
            return item.show === false;
          }
          return true;
        });
      }

      // Apply local search filter first
      if (localSearchQuery.trim()) {
        const searchTerm = localSearchQuery.toLowerCase();
        filtered = filtered.filter((item) => {
          if (item.nameIndex && Array.isArray(item.nameIndex)) {
            return item.nameIndex.some((term) =>
              term.toLowerCase().includes(searchTerm)
            );
          }
          return item.name?.toLowerCase().includes(searchTerm);
        });
      }
      // If no local search but there's a global search, apply global search filter
      else if (searchQuery.trim()) {
        const globalSearchTerm = searchQuery.toLowerCase();
        filtered = filtered.filter((item) => {
          if (item.nameIndex && Array.isArray(item.nameIndex)) {
            return item.nameIndex.some((term) =>
              term.toLowerCase().includes(globalSearchTerm)
            );
          }
          return item.name?.toLowerCase().includes(globalSearchTerm);
        });
      }

      return filtered;
    },
    [subSearchQueries, subStatusFilters, searchQuery]
  );

  // Update filtered sub-collections when sub-collections change
  useEffect(() => {
    const newFilteredSubCollections = {};
    Object.keys(subCollections).forEach((categoryId) => {
      newFilteredSubCollections[categoryId] = filterSubCollection(
        categoryId,
        subCollections[categoryId]
      );
    });
    setFilteredSubCollections(newFilteredSubCollections);
  }, [subCollections, subSearchQueries, subStatusFilters, filterSubCollection]);

  const fetchMore = useCallback(() => {
    if (lastVisible) {
      fetchData(lastVisible);
    }
  }, [fetchData, lastVisible]);

  const refresh = () => setRefreshCounter((c) => c + 1);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshCounter]);

  useEffect(() => {
    // Unsubscribe from all listeners when component unmounts
    return () => {
      Object.values(unsubscribes).forEach((unsubscribe) => unsubscribe());
    };
  }, [unsubscribes]);

  const getTechnologiesForCategory = useCallback(
    (categoryId, categoryRef) => {
      if (unsubscribes[categoryId]) {
        return; // Already listening
      }
      setLoadingSub((prev) => ({ ...prev, [categoryId]: true }));

      const unsubscribe = categoryRef
        .collection("technologies")
        .orderBy("rank")
        .onSnapshot(
          (technologiesSnapshot) => {
            const technologies = technologiesSnapshot.docs.map((doc) => ({
              ...doc.data(),
              id: doc.id,
              ref: doc.ref,
            }));
            setSubCollections((prev) => ({
              ...prev,
              [categoryId]: technologies,
            }));
            setLoadingSub((prev) => ({ ...prev, [categoryId]: false }));
          },
          (err) => {
            console.error("Error fetching technologies:", err);
            setError(err.message);
            setLoadingSub((prev) => ({ ...prev, [categoryId]: false }));
          }
        );

      setUnsubscribes((prev) => ({ ...prev, [categoryId]: unsubscribe }));
    },
    [unsubscribes]
  );

  const updateSubCollectionShowStatus = async (item) => {
    if (item && item.ref) {
      await item.ref.update({ show: !item.show });
    }
  };

  // Search and filter control functions
  const setGlobalSearch = (query) => {
    setSearchQuery(query);
  };

  const setGlobalStatusFilter = (status) => {
    setStatusFilter(status);
  };

  const setSubSearch = (categoryId, query) => {
    setSubSearchQueries((prev) => ({ ...prev, [categoryId]: query }));
  };

  const setSubStatusFilter = (categoryId, status) => {
    setSubStatusFilters((prev) => ({ ...prev, [categoryId]: status }));
  };

  return {
    stackCategories: filteredStackCategories,
    subCollections: filteredSubCollections,
    loading,
    loadingMore,
    loadingSub,
    error,
    fetchMore,
    hasMore: !!lastVisible,
    getTechnologiesForCategory,
    refresh,
    updateSubCollectionShowStatus,
    // Search and filter functions
    setGlobalSearch,
    setGlobalStatusFilter,
    setSubSearch,
    setSubStatusFilter,
    searchQuery,
    statusFilter,
    subSearchQueries,
    subStatusFilters,
  };
};

export const useSectorCollections = (pageSize = 20) => {
  const [sectors, setSectors] = useState([]);
  const [filteredSectors, setFilteredSectors] = useState([]);
  const [subCollections, setSubCollections] = useState({});
  const [filteredSubCollections, setFilteredSubCollections] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadingSub, setLoadingSub] = useState({});
  const [lastVisible, setLastVisible] = useState(null);
  const [error, setError] = useState(null);
  const [refreshCounter, setRefreshCounter] = useState(0);
  const [unsubscribes, setUnsubscribes] = useState({});

  // Search and filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [subSearchQueries, setSubSearchQueries] = useState({});
  const [subStatusFilters, setSubStatusFilters] = useState({});

  const fetchData = useCallback(
    async (startAfterDoc = null) => {
      if (startAfterDoc) setLoadingMore(true);
      else setLoading(true);
      setError(null);

      try {
        // Fetch from industry collection group to show industry items as main items
        const result = await getDocs(
          "industry",
          true, // collection group
          pageSize,
          "name",
          startAfterDoc
        );
        const newDocs = result.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
          ref: doc.ref,
          // Extract sectorId from the document path: sectors/{sectorId}/industry/{docId}
          sectorId: doc.ref.parent.parent.id,
        }));

        if (startAfterDoc) {
          setSectors((prev) => [...prev, ...newDocs]);
        } else {
          setSectors(newDocs);
        }

        if (result.docs.length > 0) {
          setLastVisible(result.docs[result.docs.length - 1]);
        } else {
          setLastVisible(null);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        if (startAfterDoc) setLoadingMore(false);
        else setLoading(false);
      }
    },
    [pageSize]
  );

  // Apply status filter helper function
  const applyStatusFilter = useCallback((items, filter) => {
    if (filter === "all") return items;

    return items.filter((item) => {
      if (filter === "active") {
        return item.show === true;
      } else if (filter === "inactive") {
        return item.show === false;
      }
      return true;
    });
  }, []);

  // Search function for main collections
  const searchCollections = useCallback(
    async (query, currentStatusFilter) => {
      if (!query.trim()) {
        // No search query, just apply status filter to all items
        const filtered = applyStatusFilter(sectors, currentStatusFilter);
        setFilteredSectors(filtered);
        return;
      }

      try {
        const searchTerm = query.toLowerCase();
        const collectionIds = new Set();

        // 1. Search collection names directly
        const matchingCollections = sectors.filter((collection) => {
          if (collection.nameIndex && Array.isArray(collection.nameIndex)) {
            return collection.nameIndex.some((term) =>
              term.toLowerCase().includes(searchTerm)
            );
          }
          return collection.name?.toLowerCase().includes(searchTerm);
        });

        matchingCollections.forEach((collection) => {
          collectionIds.add(collection.id);
        });

        // 2. Search sub-items (subSectors) across all collections using collection group
        try {
          const subSectorResults =
            await getDocumentsByArrayMembershipInCollection(
              "subSectors",
              searchTerm,
              ["nameIndex"],
              "name",
              true, // group query
              true,
              false,
              100
            );

          // Extract parent collection IDs from subSector documents
          subSectorResults.forEach((doc) => {
            const parentCollectionId = doc.ref.parent.parent.id;
            collectionIds.add(parentCollectionId);
          });
        } catch (error) {
          console.error("Error searching subSectors:", error);
        }

        // 3. Filter original collections to only include those with matching IDs
        const filteredCollections = sectors.filter((collection) =>
          collectionIds.has(collection.id)
        );

        // Apply status filter to search results
        const filtered = applyStatusFilter(
          filteredCollections,
          currentStatusFilter
        );
        setFilteredSectors(filtered);
      } catch (error) {
        console.error("Error searching collections:", error);
        setFilteredSectors([]);
      }
    },
    [sectors, applyStatusFilter]
  );

  // Filter and search effect for main collections
  useEffect(() => {
    // Always call searchCollections which handles both search and filter
    searchCollections(searchQuery, statusFilter);
  }, [sectors, statusFilter, searchQuery, searchCollections]);

  // Filter sub-collections (includes global search filter)
  const filterSubCollection = useCallback(
    (sectorId, subItems) => {
      const localSearchQuery = subSearchQueries[sectorId] || "";
      const localStatusFilter = subStatusFilters[sectorId] || "all";

      let filtered = subItems || [];

      // Apply status filter
      if (localStatusFilter !== "all") {
        filtered = filtered.filter((item) => {
          if (localStatusFilter === "active") {
            return item.show === true;
          } else if (localStatusFilter === "inactive") {
            return item.show === false;
          }
          return true;
        });
      }

      // Apply local search filter first
      if (localSearchQuery.trim()) {
        const searchTerm = localSearchQuery.toLowerCase();
        filtered = filtered.filter((item) => {
          if (item.nameIndex && Array.isArray(item.nameIndex)) {
            return item.nameIndex.some((term) =>
              term.toLowerCase().includes(searchTerm)
            );
          }
          return item.name?.toLowerCase().includes(searchTerm);
        });
      }
      // If no local search but there's a global search, apply global search filter
      else if (searchQuery.trim()) {
        const globalSearchTerm = searchQuery.toLowerCase();
        filtered = filtered.filter((item) => {
          if (item.nameIndex && Array.isArray(item.nameIndex)) {
            return item.nameIndex.some((term) =>
              term.toLowerCase().includes(globalSearchTerm)
            );
          }
          return item.name?.toLowerCase().includes(globalSearchTerm);
        });
      }

      return filtered;
    },
    [subSearchQueries, subStatusFilters, searchQuery]
  );

  // Update filtered sub-collections when sub-collections change
  useEffect(() => {
    const newFilteredSubCollections = {};
    Object.keys(subCollections).forEach((sectorId) => {
      newFilteredSubCollections[sectorId] = filterSubCollection(
        sectorId,
        subCollections[sectorId]
      );
    });
    setFilteredSubCollections(newFilteredSubCollections);
  }, [subCollections, subSearchQueries, subStatusFilters, filterSubCollection]);

  const fetchMore = useCallback(() => {
    if (lastVisible) {
      fetchData(lastVisible);
    }
  }, [fetchData, lastVisible]);

  const refresh = () => setRefreshCounter((c) => c + 1);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshCounter]);

  useEffect(() => {
    // Unsubscribe from all listeners when component unmounts
    return () => {
      Object.values(unsubscribes).forEach((unsubscribe) => unsubscribe());
    };
  }, [unsubscribes]);

  const getIndustryItems = useCallback(
    (industryId, industryRef) => {
      if (unsubscribes[industryId]) return;
      setLoadingSub((prev) => ({ ...prev, [industryId]: true }));

      const unsubscribe = industryRef.collection("subSectors").onSnapshot(
        (subSectorsSnapshot) => {
          const subSectors = subSectorsSnapshot.docs.map((doc) => ({
            ...doc.data(),
            id: doc.id,
            ref: doc.ref,
          }));
          setSubCollections((prev) => ({ ...prev, [industryId]: subSectors }));
          setLoadingSub((prev) => ({ ...prev, [industryId]: false }));
        },
        (err) => {
          console.error("Error fetching sub-sectors:", err);
          setLoadingSub((prev) => ({ ...prev, [industryId]: false }));
        }
      );
      setUnsubscribes((prev) => ({ ...prev, [industryId]: unsubscribe }));
    },
    [unsubscribes]
  );

  const updateSubCollectionShowStatus = async (item) => {
    if (item && item.ref) {
      await item.ref.update({ show: !item.show });
    }
  };

  // Search and filter control functions
  const setGlobalSearch = (query) => {
    setSearchQuery(query);
  };

  const setGlobalStatusFilter = (status) => {
    setStatusFilter(status);
  };

  const setSubSearch = (sectorId, query) => {
    setSubSearchQueries((prev) => ({ ...prev, [sectorId]: query }));
  };

  const setSubStatusFilter = (sectorId, status) => {
    setSubStatusFilters((prev) => ({ ...prev, [sectorId]: status }));
  };

  return {
    sectors: filteredSectors,
    subCollections: filteredSubCollections,
    loading,
    loadingMore,
    loadingSub,
    error,
    fetchMore,
    hasMore: !!lastVisible,
    getSubSectors: getIndustryItems,
    refresh,
    updateSubCollectionShowStatus,
    // Search and filter functions
    setGlobalSearch,
    setGlobalStatusFilter,
    setSubSearch,
    setSubStatusFilter,
    searchQuery,
    statusFilter,
    subSearchQueries,
    subStatusFilters,
  };
};

export const useCategoryCollections = (pageSize = 20) => {
  const [categories, setCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [subCollections, setSubCollections] = useState({});
  const [filteredSubCollections, setFilteredSubCollections] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadingSub, setLoadingSub] = useState({});
  const [lastVisible, setLastVisible] = useState(null);
  const [error, setError] = useState(null);
  const [refreshCounter, setRefreshCounter] = useState(0);
  const [unsubscribes, setUnsubscribes] = useState({});

  // Search and filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [subSearchQueries, setSubSearchQueries] = useState({});
  const [subStatusFilters, setSubStatusFilters] = useState({});

  const fetchData = useCallback(
    async (startAfterDoc = null) => {
      if (startAfterDoc) setLoadingMore(true);
      else setLoading(true);
      setError(null);

      try {
        const result = await getDocs(
          "categories",
          false,
          pageSize,
          "name",
          startAfterDoc
        );
        const newDocs = result.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
          ref: doc.ref,
        }));

        if (startAfterDoc) {
          setCategories((prev) => [...prev, ...newDocs]);
        } else {
          setCategories(newDocs);
        }

        if (result.docs.length > 0) {
          setLastVisible(result.docs[result.docs.length - 1]);
        } else {
          setLastVisible(null);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        if (startAfterDoc) setLoadingMore(false);
        else setLoading(false);
      }
    },
    [pageSize]
  );

  // Apply status filter helper function
  const applyStatusFilter = useCallback((items, filter) => {
    if (filter === "all") return items;

    return items.filter((item) => {
      if (filter === "active") {
        return item.show === true;
      } else if (filter === "inactive") {
        return item.show === false;
      }
      return true;
    });
  }, []);

  // Search function for main collections
  const searchCollections = useCallback(
    async (query, currentStatusFilter) => {
      if (!query.trim()) {
        // No search query, just apply status filter to all items
        const filtered = applyStatusFilter(categories, currentStatusFilter);
        setFilteredCategories(filtered);
        return;
      }

      try {
        const searchTerm = query.toLowerCase();
        const collectionIds = new Set();

        // 1. Search collection names directly
        const matchingCollections = categories.filter((collection) => {
          if (collection.nameIndex && Array.isArray(collection.nameIndex)) {
            return collection.nameIndex.some((term) =>
              term.toLowerCase().includes(searchTerm)
            );
          }
          return collection.name?.toLowerCase().includes(searchTerm);
        });

        matchingCollections.forEach((collection) => {
          collectionIds.add(collection.id);
        });

        // 2. Search sub-items (subcollections) across all collections using collection group
        try {
          const subCollectionResults =
            await getDocumentsByArrayMembershipInCollection(
              "subcollections",
              searchTerm,
              ["nameIndex"],
              "name",
              true, // group query
              true,
              false,
              100
            );

          // Extract parent collection IDs from subcollection documents
          subCollectionResults.forEach((doc) => {
            const parentCollectionId = doc.ref.parent.parent.id;
            collectionIds.add(parentCollectionId);
          });
        } catch (error) {
          console.error("Error searching subcollections:", error);
        }

        // 3. Filter original collections to only include those with matching IDs
        const filteredCollections = categories.filter((collection) =>
          collectionIds.has(collection.id)
        );

        // Apply status filter to search results
        const filtered = applyStatusFilter(
          filteredCollections,
          currentStatusFilter
        );
        setFilteredCategories(filtered);
      } catch (error) {
        console.error("Error searching collections:", error);
        setFilteredCategories([]);
      }
    },
    [categories, applyStatusFilter]
  );

  // Filter and search effect for main collections
  useEffect(() => {
    // Always call searchCollections which handles both search and filter
    searchCollections(searchQuery, statusFilter);
  }, [categories, statusFilter, searchQuery, searchCollections]);

  // Filter sub-collections (includes global search filter)
  const filterSubCollection = useCallback(
    (categoryId, subItems) => {
      const localSearchQuery = subSearchQueries[categoryId] || "";
      const localStatusFilter = subStatusFilters[categoryId] || "all";

      let filtered = subItems || [];

      // Apply status filter
      if (localStatusFilter !== "all") {
        filtered = filtered.filter((item) => {
          if (localStatusFilter === "active") {
            return item.show === true;
          } else if (localStatusFilter === "inactive") {
            return item.show === false;
          }
          return true;
        });
      }

      // Apply local search filter first
      if (localSearchQuery.trim()) {
        const searchTerm = localSearchQuery.toLowerCase();
        filtered = filtered.filter((item) => {
          if (item.nameIndex && Array.isArray(item.nameIndex)) {
            return item.nameIndex.some((term) =>
              term.toLowerCase().includes(searchTerm)
            );
          }
          return item.name?.toLowerCase().includes(searchTerm);
        });
      }
      // If no local search but there's a global search, apply global search filter
      else if (searchQuery.trim()) {
        const globalSearchTerm = searchQuery.toLowerCase();
        filtered = filtered.filter((item) => {
          if (item.nameIndex && Array.isArray(item.nameIndex)) {
            return item.nameIndex.some((term) =>
              term.toLowerCase().includes(globalSearchTerm)
            );
          }
          return item.name?.toLowerCase().includes(globalSearchTerm);
        });
      }

      return filtered;
    },
    [subSearchQueries, subStatusFilters, searchQuery]
  );

  // Update filtered sub-collections when sub-collections change
  useEffect(() => {
    const newFilteredSubCollections = {};
    Object.keys(subCollections).forEach((categoryId) => {
      newFilteredSubCollections[categoryId] = filterSubCollection(
        categoryId,
        subCollections[categoryId]
      );
    });
    setFilteredSubCollections(newFilteredSubCollections);
  }, [subCollections, subSearchQueries, subStatusFilters, filterSubCollection]);

  const fetchMore = useCallback(() => {
    if (lastVisible) {
      fetchData(lastVisible);
    }
  }, [fetchData, lastVisible]);

  const refresh = () => setRefreshCounter((c) => c + 1);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshCounter]);

  useEffect(() => {
    // Unsubscribe from all listeners when component unmounts
    return () => {
      Object.values(unsubscribes).forEach((unsubscribe) => unsubscribe());
    };
  }, [unsubscribes]);

  const getSubCollections = useCallback(
    (categoryId, categoryRef) => {
      if (unsubscribes[categoryId]) return;
      setLoadingSub((prev) => ({ ...prev, [categoryId]: true }));

      const unsubscribe = categoryRef
        .collection("subcollections")
        .orderBy("name")
        .onSnapshot(
          (subCollectionsSnapshot) => {
            const subcollections = subCollectionsSnapshot.docs.map((doc) => ({
              ...doc.data(),
              id: doc.id,
              ref: doc.ref,
            }));
            setSubCollections((prev) => ({
              ...prev,
              [categoryId]: subcollections,
            }));
            setLoadingSub((prev) => ({ ...prev, [categoryId]: false }));
          },
          (err) => {
            console.error("Error fetching sub-collections:", err);
            setLoadingSub((prev) => ({ ...prev, [categoryId]: false }));
          }
        );
      setUnsubscribes((prev) => ({ ...prev, [categoryId]: unsubscribe }));
    },
    [unsubscribes]
  );

  const updateSubCollectionShowStatus = async (item) => {
    if (item && item.ref) {
      await item.ref.update({ show: !item.show });
    }
  };

  // Search and filter control functions
  const setGlobalSearch = (query) => {
    setSearchQuery(query);
  };

  const setGlobalStatusFilter = (status) => {
    setStatusFilter(status);
  };

  const setSubSearch = (categoryId, query) => {
    setSubSearchQueries((prev) => ({ ...prev, [categoryId]: query }));
  };

  const setSubStatusFilter = (categoryId, status) => {
    setSubStatusFilters((prev) => ({ ...prev, [categoryId]: status }));
  };

  return {
    categories: filteredCategories,
    subCollections: filteredSubCollections,
    loading,
    loadingMore,
    loadingSub,
    error,
    fetchMore,
    hasMore: !!lastVisible,
    getSubCollections,
    refresh,
    updateSubCollectionShowStatus,
    // Search and filter functions
    setGlobalSearch,
    setGlobalStatusFilter,
    setSubSearch,
    setSubStatusFilter,
    searchQuery,
    statusFilter,
    subSearchQueries,
    subStatusFilters,
  };
};
